/**
 * Appointments Page — view user's bookings for their active role.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, User as UserIcon, ArrowLeft, Inbox } from "lucide-react";
import { useAuth, useLanguage, API } from "@/context/AppContext";

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/appointments`, { withCredentials: true });
        if (mounted) setItems(res.data.appointments || []);
      } catch (err) {
        console.error("Appointments load failed:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user?.user_id, user?.active_role]);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-CH"); } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8" data-testid="appointments-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="appointments-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("appointments") || "Appointments"}</h1>
            <p className="text-sm text-gray-500">{t("appointmentsSubtitle") || "Your upcoming and past bookings"}</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-gray-400">{t("loading") || "Loading…"}</div>
            ) : items.length === 0 ? (
              <div className="p-16 text-center text-gray-400" data-testid="appointments-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium text-gray-600 mb-1">
                  {t("noAppointmentsYet") || "No appointments yet"}
                </p>
                <p className="text-sm">
                  {t("noAppointmentsHint") || "When you book or receive an appointment it will appear here."}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {items.map((a, idx) => (
                  <div key={a.appointment_id || a.id || idx} className="p-4 hover:bg-gray-50" data-testid={`appointment-row-${idx}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-[#0044CC]" />
                        <span className="font-medium">{fmt(a.scheduled_at)}</span>
                      </div>
                      <Badge variant={a.status === "confirmed" ? "default" : "outline"}>
                        {a.status || "pending"}
                      </Badge>
                    </div>
                    {a.with_name && (
                      <div className="text-sm text-gray-600 flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                        {a.with_name}
                      </div>
                    )}
                    {a.duration_minutes && (
                      <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-1">
                        <Clock className="w-3 h-3" />
                        {a.duration_minutes} min
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppointmentsPage;
